import React, { useState, useEffect, useContext } from "react";
import { Tooltip, Avatar } from "antd";
import { AppstoreOutlined } from "@ant-design/icons";

import { DateFormat, getApi, saveApi } from "../../../helpers";
import CardContainer from "../../../components/Card";
import TableContainer from "../../../components/Table";
import CategorySectionPopup from "./Popup";
import CategorySectionSidebar from "./Sidebar";
import { StoreContext } from "../../../context";
import { canWrite } from "../../../helpers/permissions";

const categorySectionColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: true,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    ellipsis: {
      showTitle: true,
    },
  },
  {
    title: "Priority",
    dataIndex: "priority",
    key: "priority",
    align: "center",
    width: 100,
    sorter: true,
    render: (text) => (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: text ? '#f0f0f0' : 'transparent',
        fontWeight: text ? 600 : 'normal'
      }}>
        {text || '-'}
      </span>
    ),
  },
  {
    title: "Active Status",
    dataIndex: "is_active",
    key: "is_active",
    align: "center",
    width: 120,
    render: (text) => (
      <span
        style={{
          fontSize: '12px',
          fontWeight: 500,
          padding: "4px 12px",
          textTransform: "uppercase",
          borderRadius: '6px',
          backgroundColor: text === 1 ? '#f6ffed' : '#fff2f0',
          color: text === 1 ? '#52c41a' : '#ff4d4f',
          border: text === 1 ? '1px solid #b7eb8f' : '1px solid #ffccc7'
        }}
      >
        {text === 1 ? 'Active' : 'Inactive'}
      </span>
    ),
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
];

export default function CategorySection() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [sidebar, setSidebar] = useState(false);
  const [popup, setPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [savedPayload, setSavedPayload] = useState({});

  useEffect(() => getList(), []);

  const getList = async (payload = {}) => {
    setLoading(true);

    // Add priority-based default sorting if no sort is specified
    const requestPayload = {
      key: "JWT",
      secret: "RAPSAP",
      type: "admin",
      keyword: payload.keyword || "",
      page: payload.page || 0,
      size: payload.size || 10,
      ...payload
    };

    // If no specific sort is provided, default to priority-based sorting
    if (!requestPayload.sort) {
      requestPayload.sort = {
        field: "priority",
        order_by: "asc"
      };
    }

    const res = await getApi(requestPayload, "getCategorySection", "ecomm/");
    if (res.data) {
      setList(res.data);
      setTotal(res.meta?.total || 0);
    }
    setSavedPayload(payload);
    setLoading(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let payload = { ...savedPayload };

    if (sorter && sorter.field) {
      payload.sort = {
        field: sorter.field,
        order_by: sorter.order === 'ascend' ? 'asc' : 'desc'
      };
    } else {
      // If no sort is applied, use default priority sorting
      payload.sort = {
        field: "priority",
        order_by: "asc"
      };
    }

    getList(payload);
  };

  const saveHandler = async (values) => {
    setPopup(false);
    setSidebar(false);
    setLoading(true);

    try {
      // Prepare the category section data
      let categorySectionData = {
        key: "JWT",
        secret: "RAPSAP",
        name: values.name,
        brand_id: 1,
        description: values.description || "",
        priority: values.priority === "" ? null : parseInt(values.priority, 10) || null,
      };

      let apiName, msg;

      // For existing category section update
      if (selected.category_section_id) {
        apiName = "updateCategorySection";
        msg = "Updated Category Section";
        categorySectionData.category_section_id = selected.category_section_id;
      }
      // For new category section creation
      else {
        apiName = "createCategorySection";
        msg = "Added Category Section";
      }

      // Call API to create/update category section
      await saveApi(categorySectionData, msg, apiName, "ecomm/");

      // Refresh the category section list
      getList(savedPayload);
    } catch (error) {
      console.error("Error in category section save process:", error);
      setLoading(false);
    }
  };

  return (
    <>
      {popup && (
        <CategorySectionPopup
          title={popupTitle}
          data={selected}
          visible={popup}
          cancelHandler={() => setPopup(false)}
          saveHandler={(values) => saveHandler(values)}
        />
      )}
      {sidebar && (
        <CategorySectionSidebar
          data={selected}
          visible={sidebar}
          editCategorySection={() => (
            setPopupTitle("Edit Category Section Details"), setPopup(true)
          )}
          onClose={() => setSidebar(false)}
          onRefresh={() => getList(savedPayload)}
        />
      )}
      <CardContainer bordered={false} hoverable={false}>
        <TableContainer
          breadcrumbTitle="Category Section Management"
          loading={loading}
          total={total}
          columns={categorySectionColumns}
          data={list}
          searchFor="Category Section Name"
          addButton={canWrite() && (storeId ? false : true)}
          addHandler={() => {
            setSelected({});
            setPopupTitle("Add Category Section");
            setPopup(true);
          }}
          rowClickHandler={(record) => (
            setSelected(record),
            setSidebar(true)
          )}
          functionsHandler={getList}
          onChange={handleTableChange}
          scrollable={false}
        />
      </CardContainer>
    </>
  );
}
